import React, { useCallback, useEffect, useState } from 'react';
import {
  DollarSign, TrendingUp, TrendingDown, Wallet, Clock, AlertTriangle,
  RefreshCw, Landmark, Repeat, Users, Activity, Zap, ShieldCheck,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { apiCall } from '../../utils/api';
import { useLiveRefresh } from '../../hooks/useLiveRefresh';
import FinancialReportsPanel from './FinancialReportsPanel';
import SettlementAnalytics from './SettlementAnalytics';
import FinanceForecastPanel from './FinanceForecastPanel';
import FinanceAlertsPanel from './FinanceAlertsPanel';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Kpi = ({ label, value, sub, Icon, tone = 'text-indigo-600 bg-indigo-50', testid }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      {Icon && <span className={`p-1.5 rounded-lg ${tone}`}><Icon className="w-3.5 h-3.5" /></span>}
    </div>
    <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
    {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

const Section = ({ title, children, right }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
      {right}
    </div>
    {children}
  </div>
);

const PROVIDER_TONE = {
  live: 'bg-emerald-50 text-emerald-600',
  configured: 'bg-emerald-50 text-emerald-600',
  test: 'bg-amber-50 text-amber-600',
  missing: 'bg-red-50 text-red-500',
};

/** Phase 4 — Super-Admin Financial Command Center. Every figure comes from the
 * centralized Financial Engine (/api/admin/finance/kpis) and auto-refreshes on
 * any financial event via the live data_event stream. */
export default function FinancialCommandCenter() {
  const [kpis, setKpis] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [k, l] = await Promise.all([
        apiCall('/api/admin/finance/kpis'),
        apiCall('/api/finance/ledger?limit=15'),
      ]);
      setKpis(k.kpis);
      setLedger(l.events || []);
    } catch (e) { /* keep last snapshot on transient errors */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useLiveRefresh(['finance', 'wallet', 'payroll', 'payments', 'marketplace', 'invoices'], load);

  if (loading && !kpis) {
    return <div className="flex items-center justify-center py-16"><RefreshCw className="w-7 h-7 text-indigo-500 animate-spin" /></div>;
  }
  if (!kpis) {
    return <div className="text-center py-12 text-sm text-gray-500" data-testid="command-center-error">Unable to load financial KPIs — check backend connectivity.</div>;
  }

  const { revenue, recurring, volume, outstanding, health, commissions, growth, providers = {} } = kpis;
  const growthUp = (revenue.growth_pct ?? 0) >= 0;

  return (
    <div className="space-y-6" data-testid="financial-command-center">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Landmark className="w-5 h-5 text-indigo-600" /> Financial Command Center
          </h2>
          <p className="text-xs text-gray-400">Single source of truth — Financial Engine · live-synced · {kpis.ledger_events} ledger events</p>
        </div>
        <div className="flex items-center gap-2">
          {Object.entries(providers).map(([name, status]) => (
            <span key={name} className={`text-[11px] px-2 py-1 rounded-full font-semibold capitalize ${PROVIDER_TONE[status] || 'bg-gray-100 text-gray-500'}`} data-testid={`provider-${name}`}>
              {name}: {status}
            </span>
          ))}
        </div>
      </div>

      {/* Revenue */}
      <Section
        title="Platform Revenue"
        right={revenue.growth_pct !== null && revenue.growth_pct !== undefined && (
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${growthUp ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`} data-testid="revenue-growth-badge">
            {growthUp ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            {revenue.growth_pct}% vs last month
          </span>
        )}
      >
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Today" value={money(revenue.today)} Icon={Zap} tone="text-amber-600 bg-amber-50" testid="kpi-revenue-today" />
          <Kpi label="Last 7 Days" value={money(revenue.last_7_days)} Icon={Activity} testid="kpi-revenue-7d" />
          <Kpi label="Month to Date" value={money(revenue.month_to_date)} sub={`Prev month ${money(revenue.prev_month)}`} Icon={TrendingUp} tone="text-emerald-600 bg-emerald-50" testid="kpi-revenue-mtd" />
          <Kpi label="Year to Date" value={money(revenue.year_to_date)} Icon={Landmark} testid="kpi-revenue-ytd" />
          <Kpi label="All Time" value={money(revenue.all_time)} Icon={DollarSign} tone="text-indigo-600 bg-indigo-50" testid="kpi-revenue-all" />
        </div>
      </Section>

      {/* Trend chart + revenue mix */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4" data-testid="revenue-trend-chart">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Revenue Trend — 12 Weeks</h4>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={kpis.trend_weeks || []}>
              <defs>
                <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="week_start" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={money0} width={56} />
              <Tooltip formatter={(v, n) => [money(v), n]} labelFormatter={(d) => `Week of ${d}`} />
              <Area type="monotone" dataKey="total" name="Total" stroke="#6366f1" fill="url(#revFill)" strokeWidth={2} />
              <Area type="monotone" dataKey="payroll_fees" name="Payroll fees" stroke="#10b981" fill="none" strokeWidth={1.5} />
              <Area type="monotone" dataKey="marketplace" name="Marketplace" stroke="#f59e0b" fill="none" strokeWidth={1.5} />
              <Area type="monotone" dataKey="subscriptions" name="Subscriptions" stroke="#8b5cf6" fill="none" strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="revenue-mix">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Revenue Mix (all time)</h4>
          {[
            ['Payroll fees', revenue.by_source.payroll_fees, 'bg-emerald-500'],
            ['Marketplace commissions', revenue.by_source.marketplace_commissions, 'bg-amber-500'],
            ['Subscriptions', revenue.by_source.subscriptions, 'bg-violet-500'],
            ['Instant-pay fees', revenue.by_source.instant_pay_fees, 'bg-sky-500'],
          ].map(([label, val, color]) => {
            const pct = revenue.all_time > 0 ? Math.round((val / revenue.all_time) * 100) : 0;
            return (
              <div key={label} className="mb-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600">{label}</span>
                  <span className="font-semibold text-gray-800">{money(val)} · {pct}%</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full"><div className={`h-1.5 rounded-full ${color}`} style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
          <div className="pt-2 mt-2 border-t border-gray-100 flex justify-between text-sm">
            <span className="text-gray-500 flex items-center gap-1"><Repeat className="w-3.5 h-3.5" /> MRR</span>
            <span className="font-bold text-gray-900" data-testid="kpi-mrr">{money(recurring.mrr)} · {recurring.active_subscriptions} subs</span>
          </div>
        </div>
      </div>

      {/* Volume + Outstanding */}
      <Section title="Volume & Outstanding">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Kpi label="Payroll Volume" value={money(volume.payroll_volume_paid)} sub={`${volume.payroll_paid_count} payments`} Icon={DollarSign} tone="text-emerald-600 bg-emerald-50" testid="kpi-payroll-volume" />
          <Kpi label="Avg Payroll" value={money(volume.avg_payroll_value)} Icon={Activity} testid="kpi-avg-payroll" />
          <Kpi label="Marketplace Gross" value={money(volume.marketplace_gross)} sub={`${volume.marketplace_settled_orders} settled`} Icon={Landmark} tone="text-amber-600 bg-amber-50" testid="kpi-marketplace-gross" />
          <Kpi label="Avg Transaction" value={money(volume.avg_transaction_value)} sub={`${volume.wallet_transactions} wallet txns`} Icon={Wallet} testid="kpi-avg-tx" />
          <Kpi label="Pending Payroll" value={money(outstanding.pending_payroll_total)} sub={`${outstanding.pending_payroll_count} shifts`} Icon={Clock} tone="text-amber-600 bg-amber-50" testid="kpi-pending-payroll" />
          <Kpi label="Pending Settlements" value={money(outstanding.pending_settlements_total)} sub={`${outstanding.pending_settlements_count} orders · seats ${money(outstanding.seat_invoices_outstanding)}`} Icon={Clock} tone="text-orange-600 bg-orange-50" testid="kpi-pending-settlements" />
        </div>
      </Section>

      {/* Health + Commissions + Growth */}
      <Section title="Financial Health & Growth">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Kpi label="Wallet Liability" value={money(health.wallet_liability)} sub={`${health.wallet_count} wallets`} Icon={Wallet} tone="text-blue-600 bg-blue-50" testid="kpi-wallet-liability" />
          <Kpi label="Cash Flow (MTD)" value={money(health.cash_flow_month.net)} sub={`in ${money(health.cash_flow_month.inflow)} / out ${money(health.cash_flow_month.outflow)}`} Icon={Activity} tone={health.cash_flow_month.net >= 0 ? 'text-emerald-600 bg-emerald-50' : 'text-red-600 bg-red-50'} testid="kpi-cash-flow" />
          <Kpi label="Payment Success" value={health.payment_success_rate === null ? '—' : `${health.payment_success_rate}%`} sub={`${health.payment_failures} failures`} Icon={ShieldCheck} tone="text-emerald-600 bg-emerald-50" testid="kpi-payment-success" />
          <Kpi label="Refund / Chargeback" value={`${health.refund_rate}% / ${health.chargeback_rate}%`} sub={`${health.refunds} refunds · ${health.chargebacks} chargebacks`} Icon={AlertTriangle} tone="text-red-600 bg-red-50" testid="kpi-refund-chargeback" />
          <Kpi label="Agency Commissions" value={money(commissions.agency_earned)} sub={`${money(commissions.agency_pending)} pending`} Icon={Landmark} tone="text-purple-600 bg-purple-50" testid="kpi-agency-commissions" />
          <Kpi
            label="Growth (new this month)"
            value={`+${Object.values(growth.new_this_month_by_role || {}).reduce((a, b) => a + b, 0)}`}
            sub={`W ${growth.new_this_month_by_role?.worker || 0} · E ${growth.new_this_month_by_role?.employer || 0} · A ${growth.new_this_month_by_role?.agent || 0}`}
            Icon={Users} tone="text-teal-600 bg-teal-50" testid="kpi-growth"
          />
        </div>
      </Section>

      {/* Live ledger feed */}
      <div className="bg-white rounded-xl border border-gray-200" data-testid="live-ledger-feed">
        <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
            Live Financial Events
          </h4>
          <span className="text-[11px] text-gray-400">auto-updates on every ledger write</span>
        </div>
        <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
          {ledger.length === 0 ? (
            <p className="px-4 py-6 text-xs text-gray-400 text-center">No financial events yet.</p>
          ) : ledger.map((e) => (
            <div key={e._id || `${e.event_type}-${e.created_at}`} className="px-4 py-2 flex items-center gap-3 text-xs">
              <span className={`px-2 py-0.5 rounded-full font-semibold ${e.direction === 'credit' ? 'bg-emerald-50 text-emerald-600' : e.direction === 'debit' ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-500'}`}>{e.event_type}</span>
              <span className="text-gray-600 truncate flex-1">{e.description}</span>
              <span className="font-semibold text-gray-800">{money(e.amount)}</span>
              <span className="text-gray-400 whitespace-nowrap">{e.created_at ? new Date(e.created_at).toLocaleString() : ''}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Settlement engine trends */}
      <SettlementAnalytics />

      {/* Phase 4 Wave C: forecast + AI narrative */}
      <FinanceForecastPanel />

      {/* Phase 4 Wave C: monitoring alerts + reconciliation */}
      <FinanceAlertsPanel />

      {/* Phase 4 Wave B: centralized reports */}
      <FinancialReportsPanel />
    </div>
  );
}
